import { useEffect, useMemo, useRef, useState } from 'react';
import type { I18nKey } from '@brao/shared';
import { ApiError } from '../api';
import {
  approvalsApi,
  type Approval,
  type ApprovalDecision,
  type ApprovalKind,
  type ApprovalListResult,
  type ApprovalMetrics,
  type ApprovalRisk,
  type ApprovalSort,
} from '../api/approvals';
import { Table, type Column, type TableSort } from '../components/Table';
import { t } from '../i18n';
import { Link } from '../router';
import './approvals.css';

const PAGE_SIZE = 25;
/** Kinds phase 2 can produce; the chips filter the queue by them (`plan`, `new_agent` come later). */
const KIND_CHIPS: ApprovalKind[] = ['step', 'external_action'];
/** Column keys of the decided table that are also the server's sort names. */
const SERVER_SORTS: ApprovalSort[] = ['requestedAt', 'decidedAt', 'kind'];

const fmt = (iso: string) => new Date(iso).toLocaleString('uk-UA', { dateStyle: 'short', timeStyle: 'short' });

/**
 * Enum → key maps instead of interpolated keys: the i18n guard only sees plain string literals in a
 * `t(...)` call, so a key built with `${}` would never be checked (scripts/guards/i18n-keys.mjs).
 */
const KIND_KEY: Record<ApprovalKind, I18nKey> = {
  plan: 'approvals.kind.plan',
  step: 'approvals.kind.step',
  external_action: 'approvals.kind.externalAction',
  new_agent: 'approvals.kind.newAgent',
  knowledge: 'approvals.kind.knowledge',
};
const RISK_KEY: Record<ApprovalRisk, I18nKey> = { low: 'approvals.risk.low', medium: 'approvals.risk.medium', high: 'approvals.risk.high' };
const RISK_CLASS: Record<ApprovalRisk, string> = { low: 'badge', medium: 'badge warn', high: 'badge danger' };
const DECISION_KEY: Record<ApprovalDecision, I18nKey> = {
  approved: 'approvals.decision.approved',
  rework: 'approvals.decision.rework',
  rejected: 'approvals.decision.rejected',
};
const DECISION_CLASS: Record<ApprovalDecision, string> = { approved: 'badge ok', rework: 'badge warn', rejected: 'badge danger' };
/** Machine-readable API errors of the approvals endpoints, in Ukrainian. */
const ERROR_KEY: Record<string, I18nKey> = {
  already_decided: 'approvals.error.alreadyDecided',
  expired: 'approvals.error.expired',
  reason_required: 'approvals.error.reasonRequired',
  decision_not_applicable: 'approvals.error.notApplicable',
  budget_hard_limit: 'approvals.error.budgetHardLimit',
  instance_not_active: 'approvals.error.instanceNotActive',
  invalid_execution_status: 'approvals.error.invalidStatus',
  approval_not_found: 'approvals.error.notFound',
};

function errorText(e: unknown): string {
  const code = e instanceof ApiError ? e.code : e instanceof Error ? e.message : '';
  const key = ERROR_KEY[code];
  return key ? t(key) : code || t('common.error');
}

/** 409 and 410 both mean "the world moved on": the card is gone and the list must be reloaded. */
const isConflict = (e: unknown) => e instanceof ApiError && (e.status === 409 || e.status === 410);

/**
 * The badge of a request: the kind, refined by the reason the engine wrote into the payload — a
 * cost threshold and a retry in flight are both `external_action` rows but read very differently.
 */
function kindKey(row: Approval): I18nKey {
  if (row.kind !== 'external_action') return KIND_KEY[row.kind];
  if (row.subject.reason === 'cost_threshold') return 'approvals.kind.cost';
  if (row.subject.reason === 'retry_in_flight') return 'approvals.kind.retry';
  return KIND_KEY.external_action;
}

const ago = (minutes: number): string =>
  minutes < 1 ? t('approvals.age.now') : minutes < 60 ? t('approvals.age.minutes', { n: minutes }) : minutes < 1440 ? t('approvals.age.hours', { n: Math.floor(minutes / 60) }) : t('approvals.age.days', { n: Math.floor(minutes / 1440) });

const duration = (minutes: number): string =>
  minutes < 60 ? t('approvals.dur.minutes', { n: minutes }) : minutes < 1440 ? t('approvals.dur.hours', { n: Math.floor(minutes / 60) }) : t('approvals.dur.days', { n: Math.floor(minutes / 1440) });

/**
 * Затвердження (mockup `docs/mockups/phase-2/approvals.html`): the KPI strip, the queue of pending
 * cards with three buttons each, and the table of decided requests below. The decision goes to
 * `POST /approvals/:id/decide`, which is the same service the Telegram bot calls — so a request
 * decided in Telegram while this screen was open answers 409 here, and the conflict banner (the
 * phase-1 pattern) asks for a reload instead of pretending the click worked.
 */
export function Approvals({ canDecide, onPendingCount }: { canDecide: boolean; onPendingCount?: (n: number) => void }) {
  const [metrics, setMetrics] = useState<ApprovalMetrics | null>(null);
  const [businesses, setBusinesses] = useState<{ id: string; name: string; status: string }[]>([]);
  const [businessId, setBusinessId] = useState('');
  const [kind, setKind] = useState<ApprovalKind | ''>('');
  const [pending, setPending] = useState<Approval[] | null>(null);
  const [decided, setDecided] = useState<ApprovalListResult | null>(null);
  const [sort, setSort] = useState<TableSort | null>(null);
  const [page, setPage] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // Every list request gets a sequence number; a response that is not the latest one is dropped.
  const seq = useRef(0);

  useEffect(() => {
    approvalsApi.businesses().then(setBusinesses).catch((e) => setError(errorText(e)));
  }, []);

  useEffect(() => {
    setPage(1);
  }, [businessId, kind, sort]);

  const load = () => {
    const mine = ++seq.current;
    return Promise.all([
      approvalsApi.list({ decision: 'pending', businessId: businessId || undefined, kind: kind || undefined, pageSize: PAGE_SIZE }),
      approvalsApi.list({
        decision: 'decided',
        businessId: businessId || undefined,
        kind: kind || undefined,
        sort: sort ? (sort.key as ApprovalSort) : undefined,
        dir: sort?.dir,
        page,
        pageSize: PAGE_SIZE,
      }),
      approvalsApi.metrics(),
    ])
      .then(([queue, done, m]) => {
        if (mine !== seq.current) return;
        setPending(queue.rows);
        setDecided(done);
        setMetrics(m);
        onPendingCount?.(m.pending);
      })
      .catch((e) => {
        if (mine === seq.current) setError(errorText(e));
      });
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId, kind, page, sort]);

  const reload = () => {
    setConflict(null);
    setError(null);
    void load();
  };

  /**
   * Optimistic: the card leaves the queue the moment the decision is sent. A 409 / 410 means
   * someone else decided it — the banner then offers the reload; any other error puts the card
   * back, because the request is still waiting.
   */
  const decide = async (row: Approval, decision: ApprovalDecision, reason: string) => {
    setError(null);
    setNotice(null);
    setPending((rows) => (rows ?? []).filter((r) => r.id !== row.id));
    try {
      const result = await approvalsApi.decide(row.id, decision, reason || undefined);
      onPendingCount?.(result.pending);
      if (result.newExecutionId) setNotice(t('approvals.reworkStarted'));
      void load();
    } catch (e) {
      if (isConflict(e)) {
        setConflict(errorText(e));
        return;
      }
      setPending((rows) => (rows ? [...rows, row].sort((a, b) => a.requestedAt.localeCompare(b.requestedAt)) : rows));
      throw e;
    }
  };

  const columns: Column<Approval>[] = [
    { key: 'decidedAt', title: t('approvals.col.when'), render: (r) => <span className="muted">{fmt(r.decidedAt ?? r.expiresAt ?? r.requestedAt)}</span>, sortable: true },
    { key: 'kind', title: t('approvals.col.kind'), render: (r) => t(kindKey(r)), sortable: true },
    { key: 'agent', title: t('approvals.col.agent'), render: (r) => r.subject.agentName ?? '—' },
    { key: 'task', title: t('approvals.col.task'), render: (r) => (r.subject.taskId ? <Link to={`/tasks/${r.subject.taskId}`}>{r.subject.taskKey ?? '—'}</Link> : (r.subject.taskKey ?? '—')) },
    {
      key: 'decision',
      title: t('approvals.col.decision'),
      render: (r) => (r.decision ? <span className={DECISION_CLASS[r.decision]}>{t(DECISION_KEY[r.decision])}</span> : <span className="badge">{t('approvals.decision.expired')}</span>),
    },
    { key: 'channel', title: t('approvals.col.channel'), render: (r) => (r.channel === 'telegram' ? t('approvals.channel.telegram') : r.channel === 'mcp' ? t('approvals.channel.mcp') : r.channel === 'panel' ? t('approvals.channel.panel') : '—') },
    { key: 'who', title: t('approvals.col.who'), render: (r) => r.approver?.name ?? '—' },
  ];

  const activeBusinesses = useMemo(() => businesses.filter((b) => b.status === 'active' || b.id === businessId), [businesses, businessId]);
  const total = decided?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <>
      <h1>{t('approvals.title')}</h1>
      <p className="muted">{t('approvals.intro')}</p>

      {conflict && (
        <div className="conflict" role="alert">
          <span>{conflict}</span>
          <button type="button" className="btn secondary small" onClick={reload}>{t('approvals.reload')}</button>
        </div>
      )}
      {error && <div className="error" style={{ marginBottom: 'var(--sp-3)' }}>{error}</div>}
      {notice && <div className="note" style={{ marginBottom: 'var(--sp-3)' }}>{notice}</div>}

      <div className="kpi" style={{ marginBottom: 'var(--sp-6)' }}>
        <div>
          <span className="muted">{t('approvals.kpi.pending')}</span>
          <strong>{metrics?.pending ?? 0}</strong>
        </div>
        <div>
          <span className="muted">{t('approvals.kpi.oldest')}</span>
          <strong>{metrics?.oldestPendingMinutes === null || metrics === null ? t('approvals.kpi.none') : duration(metrics.oldestPendingMinutes)}</strong>
        </div>
        <div>
          <span className="muted">{t('approvals.kpi.decided7')}</span>
          <strong>{metrics?.decidedLast7Days ?? 0}</strong>
        </div>
      </div>

      <div className="row filters" style={{ marginBottom: 'var(--sp-3)' }}>
        <select value={businessId} onChange={(e) => setBusinessId(e.target.value)} aria-label={t('approvals.filter.allBusinesses')}>
          <option value="">{t('approvals.filter.allBusinesses')}</option>
          {activeBusinesses.map((b) => (
            <option key={b.id} value={b.id}>{b.name}</option>
          ))}
        </select>
        <button type="button" className={`chip${kind === '' ? ' on' : ''}`} aria-pressed={kind === ''} onClick={() => setKind('')}>{t('approvals.filter.allKinds')}</button>
        {KIND_CHIPS.map((k) => (
          <button key={k} type="button" className={`chip${kind === k ? ' on' : ''}`} aria-pressed={kind === k} onClick={() => setKind(k)}>
            {t(KIND_KEY[k])} {metrics?.byKind[k] ?? 0}
          </button>
        ))}
      </div>

      <h2>{t('approvals.pending')}</h2>
      {pending !== null && pending.length === 0 && <p className="muted">{t('approvals.empty.pending')}</p>}
      {(pending ?? []).map((row) => (
        <ApprovalCard key={row.id} row={row} canDecide={canDecide} onDecide={decide} />
      ))}

      <h2>{t('approvals.decided')}</h2>
      <Table
        columns={columns}
        rows={decided?.rows ?? []}
        rowKey={(r) => r.id}
        empty={t('approvals.empty.decided')}
        sort={sort}
        onSort={(s) => setSort(s && SERVER_SORTS.includes(s.key as ApprovalSort) ? s : null)}
      />
      <div className="row between" style={{ marginTop: 'var(--sp-3)' }}>
        <span className="muted">{t('approvals.shown', { shown: decided?.rows.length ?? 0, total, page })}</span>
        {pages > 1 && (
          <span className="row">
            <button type="button" className="btn secondary small" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>{t('approvals.prev')}</button>
            <button type="button" className="btn secondary small" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>{t('approvals.next')}</button>
          </span>
        )}
      </div>
    </>
  );
}

/** One waiting request: head, body by kind, the comment field and the three buttons. */
function ApprovalCard({ row, canDecide, onDecide }: { row: Approval; canDecide: boolean; onDecide: (row: Approval, decision: ApprovalDecision, reason: string) => Promise<void> }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (decision: ApprovalDecision) => {
    // the same rule the server enforces (`ReasonRequired` → 400), answered without a round trip
    if (decision === 'rework' && !reason.trim()) {
      setError(t('approvals.error.reasonRequired'));
      return;
    }
    setError(null);
    setBusy(true);
    try {
      await onDecide(row, decision, reason.trim());
    } catch (e) {
      setError(errorText(e));
      setBusy(false);
    }
  };

  return (
    <div className="card stack approval-card">
      <div className="row between">
        <div className="row">
          <span className="badge">{t(kindKey(row))}</span>
          {row.business && <span className="chip">{row.business.name}</span>}
          {row.subject.agentName && <span className="muted">{row.subject.agentName}</span>}
          {row.subject.taskId ? <Link to={`/tasks/${row.subject.taskId}`}>{row.subject.taskKey ?? row.subject.taskId}</Link> : row.subject.taskKey && <span className="muted">{row.subject.taskKey}</span>}
        </div>
        <div className="row">
          <span className={RISK_CLASS[row.risk]}>{t(RISK_KEY[row.risk])}</span>
          <span className="muted">{ago(row.ageMinutes)}</span>
        </div>
      </div>

      <ApprovalBody row={row} />

      {row.expiresAt && <p className="muted" style={{ margin: 0 }}>{t('approvals.expires', { when: fmt(row.expiresAt) })}</p>}

      <label className="field">
        {t('approvals.comment')}
        <textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder={t('approvals.comment.placeholder')} disabled={!canDecide || busy} />
      </label>
      {error && <div className="error">{error}</div>}
      <div className="row approval-actions">
        <button type="button" className="btn" disabled={!canDecide || busy} onClick={() => void submit('approved')}>{t('approvals.approve')}</button>
        <button type="button" className="btn secondary" disabled={!canDecide || busy} onClick={() => void submit('rework')}>{t('approvals.rework')}</button>
        <button type="button" className="btn secondary" disabled={!canDecide || busy} onClick={() => void submit('rejected')}>{t('approvals.reject')}</button>
      </div>
    </div>
  );
}

/** What the human has to judge: the draft, the tool call with its arguments, or the two amounts. */
function ApprovalBody({ row }: { row: Approval }) {
  const { subject } = row;

  if (subject.toolName) {
    const description = subject.reason === 'retry_in_flight' ? t('approvals.desc.retry', { tool: subject.toolName, attempt: subject.attempt ?? 1 }) : t('approvals.desc.tool', { tool: subject.toolName });
    return (
      <>
        <p className="muted" style={{ margin: 0 }}>{description}</p>
        <pre className="codeblock">{JSON.stringify(subject.args ?? {}, null, 2)}</pre>
      </>
    );
  }

  if (subject.reason === 'cost_threshold') {
    return (
      <>
        <p className="muted" style={{ margin: 0 }}>{t('approvals.desc.cost')}</p>
        <div className="row approval-figures">
          <div>
            <span className="muted">{t('approvals.estimate')}</span>
            <strong>{subject.estimatedCost ?? '—'}</strong>
          </div>
          <div>
            <span className="muted">{t('approvals.threshold')}</span>
            <strong>{subject.threshold ?? t('approvals.noThreshold')}</strong>
          </div>
          {subject.cost !== undefined && Number(subject.cost) > 0 && (
            <div>
              <span className="muted">{t('approvals.cost')}</span>
              <strong>{subject.cost}</strong>
            </div>
          )}
        </div>
      </>
    );
  }

  return (
    <>
      <p className="muted" style={{ margin: 0 }}>{t('approvals.desc.step')}</p>
      <div className="approval-draft">{subject.text ?? ''}</div>
      {subject.textTruncated && <p className="note" style={{ margin: 0 }}>{t('approvals.textTruncated')}</p>}
    </>
  );
}
